import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputField, Modal } from '../UI';
import { Button } from '../UI/base/Button';
import modalStyles from '../UI/Modal.module.css';
import type { StaffProfessionResponse } from '../../api/generated';
import styles from './StaffCompetenceFormModal.module.css';

export interface StaffCompetenceFormValues {
  name: string;
  showInKpi: boolean;
}

interface StaffCompetenceFormModalProps {
  competence: StaffProfessionResponse | null;
  onSave: (values: StaffCompetenceFormValues) => void;
  onClose: () => void;
}

const emptyValues = (): StaffCompetenceFormValues => ({
  name: '',
  showInKpi: true
});

const responseToValues = (c: StaffProfessionResponse): StaffCompetenceFormValues => ({
  name: c.name,
  showInKpi: c.showInKpi
});

export const StaffCompetenceFormModal = ({
  competence,
  onSave,
  onClose
}: StaffCompetenceFormModalProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<StaffCompetenceFormValues>(
    competence ? responseToValues(competence) : emptyValues()
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = t('config-sc-error-name');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({
      name: form.name.trim(),
      showInKpi: form.showInKpi
    });
  };

  return (
    <Modal
      title={competence ? t('config-sc-edit') : t('config-sc-create')}
      onClose={onClose}
      width={520}
      footer={
        <div className={modalStyles.footerRow}>
          <Button type="secondary" onClick={onClose}>
            {t('save-modal-cancel')}
          </Button>
          <Button type="primary" onClick={handleSave}>
            {t('config-iv-save')}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        <InputField
          label={t('config-sc-name')}
          required
          type="text"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          error={errors.name}
        />
        <label className={styles.toggleRow}>
          <input
            type="checkbox"
            checked={form.showInKpi}
            onChange={(e) => setForm((f) => ({ ...f, showInKpi: e.target.checked }))}
          />
          <span>{t('config-sc-show-in-kpi')}</span>
        </label>
      </div>
    </Modal>
  );
};
